import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminGetOrders, adminGetProducts, adminGetSettings, adminUpdateOrder, adminExportAuspostCsv, adminSetOrderPackaging, adminSetItemPreorder, adminAddFreebie, adminRemoveFreebie } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { ToastAction } from '../../components/ui/toast';
import { RefreshCw, PackageCheck, ShoppingBag, CheckCircle2, Circle, Download, ChevronRight, ChevronLeft, Clock, Gift, Check, Printer } from 'lucide-react';
import PackFocusMode, { PRESETS } from './PackFocusMode';
import { printPackingSlip, printPackingSlips } from './packingSlip';
import { QuickTracking } from './QuickTracking';

const GRAB_KEY = 'alp_pick_grabbed';
const PACK_KEY = 'alp_packed_items';

const defaultPkg = (o) => o.packaging || { type: 'Pack', ...PRESETS.Pack };
const readStore = (key) => { try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch (e) { return {}; } };
const writeStore = (key, obj) => { try { localStorage.setItem(key, JSON.stringify(obj)); } catch (e) {} };
const physicalItems = (o) => (o.items || []).filter((it) => !it.digital && (it.qty || 0) > 0);
const itemKey = (orderNumber, it) => `${orderNumber}::${it.id}::${it.size || ''}`;

const STEPS = [
  { n: 1, label: 'Pick', sub: 'Grab stock out' },
  { n: 2, label: 'Pack', sub: 'One order at a time' },
  { n: 3, label: 'Export', sub: 'Australia Post CSV' },
];

export default function FulfillmentPanel({ onOrderShipped }) {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [focusOrderNumber, setFocusOrderNumber] = useState(null);
  const [grabbed, setGrabbed] = useState(() => readStore(GRAB_KEY));
  const [packed, setPacked] = useState(() => readStore(PACK_KEY));
  const [pkgDraft, setPkgDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const didInit = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetOrders();
      setOrders(data);
      setPkgDraft((prev) => {
        const next = { ...prev };
        data.forEach((o) => { if (!next[o.orderNumber]) next[o.orderNumber] = defaultPkg(o); });
        return next;
      });
      if (!didInit.current) {
        didInit.current = true;
        const q = data.filter((o) => o.orderStatus === 'Payment Confirmed' && physicalItems(o).length > 0);
        if (q.length === 0) setStep(3);
      }
    } catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminGetProducts().then((ps) => setProducts((ps || []).filter((p) => !p.digital).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {}); }, []);
  const [slipNote, setSlipNote] = useState('');
  useEffect(() => { adminGetSettings().then((s) => setSlipNote(s.slipNote || '')).catch(() => {}); }, []);

  useEffect(() => { writeStore(GRAB_KEY, grabbed); }, [grabbed]);
  useEffect(() => { writeStore(PACK_KEY, packed); }, [packed]);

  const replaceOrder = (updated) => setOrders((prev) => prev.map((o) => (o.orderNumber === updated.orderNumber ? updated : o)));

  // ---- Packaging ----
  const savePackaging = async (orderNumber, pkg) => {
    setPkgDraft((p) => ({ ...p, [orderNumber]: pkg }));
    try { replaceOrder(await adminSetOrderPackaging(orderNumber, pkg)); }
    catch (e) { toast({ title: 'Could not save packaging', variant: 'destructive' }); }
  };
  const setPreset = (orderNumber, type) => savePackaging(orderNumber, { type, ...PRESETS[type] });
  const setPkgField = (orderNumber, field, value) => setPkgDraft((p) => ({ ...p, [orderNumber]: { ...(p[orderNumber] || {}), type: 'Custom', [field]: value } }));
  const commitPkgField = (orderNumber) => {
    const d = pkgDraft[orderNumber];
    if (!d) return;
    savePackaging(orderNumber, {
      type: d.type || 'Custom',
      length: Number(d.length) || 0, width: Number(d.width) || 0,
      height: Number(d.height) || 0, weight: Number(d.weight) || 0,
    });
  };

  // ---- Export ----
  const [exporting, setExporting] = useState(false);
  const packedReady = orders.filter((o) => o.orderStatus === 'Packed' && !o.auspostExportedAt && physicalItems(o).length > 0);
  const alreadyExported = orders.filter((o) => o.orderStatus === 'Packed' && o.auspostExportedAt).length;
  const exportAuspost = async (onlyNew = true) => {
    setExporting(true);
    try {
      const res = await adminExportAuspostCsv('Packed', onlyNew);
      const count = res.headers['x-order-count'];
      if (!count || count === '0') {
        toast({ title: 'Nothing new to export', description: 'All packed orders have already been lodged with Australia Post.' });
        setExporting(false); return;
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `auspost-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast({ title: 'Australia Post CSV downloaded', description: `${count} packed order(s) exported. Upload it in MyPost Business \u2192 Import orders.` });
      await load();
    } catch (e) { toast({ title: 'Export failed', variant: 'destructive' }); }
    finally { setExporting(false); }
  };

  // ---- Queue / pick list ----
  const queue = orders.filter((o) => o.orderStatus === 'Payment Confirmed' && !o.preorderHold && physicalItems(o).length > 0);
  const heldOrders = orders.filter((o) => o.orderStatus === 'Payment Confirmed' && o.preorderHold && physicalItems(o).length > 0);
  const pickMap = {};
  queue.forEach((o) => {
    physicalItems(o).filter((it) => !it.preorder).forEach((it) => {
      const key = `${it.id}::${it.size || ''}`;
      if (!pickMap[key]) pickMap[key] = { key, name: it.name, size: it.size || '', totalQty: 0, orderCount: 0 };
      pickMap[key].totalQty += (it.qty || 0);
      pickMap[key].orderCount += 1;
    });
  });
  const pickList = Object.values(pickMap).sort((a, b) => a.name.localeCompare(b.name));
  const grabbedCount = pickList.filter((p) => grabbed[p.key]).length;
  const toggleGrab = (key) => setGrabbed((g) => ({ ...g, [key]: !g[key] }));
  const resetGrab = () => setGrabbed({});

  const orderProgress = (o) => {
    const req = physicalItems(o).filter((it) => !it.preorder);
    return { done: req.filter((it) => packed[itemKey(o.orderNumber, it)]).length, total: req.length };
  };

  // ---- Focus mode actions ----
  const toggleItem = (order, it) => setPacked((p) => ({ ...p, [itemKey(order.orderNumber, it)]: !p[itemKey(order.orderNumber, it)] }));

  const togglePreorder = async (order, it) => {
    try {
      const updated = await adminSetItemPreorder(order.orderNumber, it.id, it.size || '', !it.preorder);
      replaceOrder(updated);
      toast({ title: !it.preorder ? `${it.name} marked as preorder` : `${it.name} back in this parcel`, description: !it.preorder ? 'It ships later and stock has been adjusted.' : 'Stock deducted from current inventory.' });
    } catch (e) { toast({ title: 'Could not update item', variant: 'destructive' }); }
  };

  const addFreebie = async (orderNumber, productId, qty) => {
    try {
      replaceOrder(await adminAddFreebie(orderNumber, productId, qty));
      toast({ title: 'Freebie added', description: 'Stock deducted so your counts stay accurate.' });
    } catch (e) { toast({ title: 'Could not add freebie', variant: 'destructive' }); }
  };
  const removeFreebie = async (orderNumber, freebieId) => {
    try {
      replaceOrder(await adminRemoveFreebie(orderNumber, freebieId));
      toast({ title: 'Freebie removed', description: 'Stock restored.' });
    } catch (e) { toast({ title: 'Could not remove freebie', variant: 'destructive' }); }
  };

  const undoPack = async (orderNumber, keysToClear) => {
    try {
      await adminUpdateOrder(orderNumber, { orderStatus: 'Payment Confirmed' });
      setPacked((p) => { const n = { ...p }; keysToClear.forEach((k) => { delete n[k]; }); return n; });
      await load();
      setStep(2);
    } catch (e) { toast({ title: 'Undo failed', variant: 'destructive' }); }
  };

  const holdOrder = async (order) => {
    setBusy(true);
    try {
      const updated = await adminUpdateOrder(order.orderNumber, { preorderHold: true });
      replaceOrder(updated);
      const remaining = queue.filter((o) => o.orderNumber !== order.orderNumber);
      toast({ title: `${order.orderNumber} moved to the preorder list`, description: 'Pack it later once the preorder stock lands.' });
      if (focusOrderNumber === order.orderNumber) {
        if (remaining.length > 0) setFocusOrderNumber(remaining[0].orderNumber);
        else setFocusOrderNumber(null);
      }
    } catch (e) { toast({ title: 'Could not move order', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const releaseOrder = async (order, packNow = false) => {
    try {
      const updated = await adminUpdateOrder(order.orderNumber, { preorderHold: false });
      replaceOrder(updated);
      toast({ title: `${order.orderNumber} back in the packing queue` });
      if (packNow) setFocusOrderNumber(order.orderNumber);
    } catch (e) { toast({ title: 'Could not move order', variant: 'destructive' }); }
  };

  const completeOrder = async (order) => {
    setBusy(true);
    try {
      const updated = await adminUpdateOrder(order.orderNumber, { orderStatus: 'Packed' });
      const keys = physicalItems(order).map((x) => itemKey(order.orderNumber, x));
      setPacked((p) => { const n = { ...p }; keys.forEach((k) => { delete n[k]; }); return n; });
      const remaining = queue.filter((o) => o.orderNumber !== order.orderNumber);
      setOrders((prev) => prev.map((o) => (o.orderNumber === order.orderNumber ? updated : o)));
      toast({
        title: `${order.orderNumber} packed \u2713`,
        description: remaining.length > 0 ? `${remaining.length} order${remaining.length === 1 ? '' : 's'} to go.` : 'All orders packed! Ready to export.',
        action: <ToastAction altText="Undo" onClick={() => undoPack(order.orderNumber, keys)}>Undo</ToastAction>,
      });
      if (remaining.length > 0) {
        setFocusOrderNumber(remaining[0].orderNumber);
      } else {
        setFocusOrderNumber(null);
        setStep(3);
      }
    } catch (e) { toast({ title: 'Could not update order', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const focusOrder = focusOrderNumber ? orders.find((o) => o.orderNumber === focusOrderNumber) : null;
  const focusPosition = focusOrder ? queue.findIndex((o) => o.orderNumber === focusOrderNumber) + 1 : 0;

  // ---- Stepper ----
  const stepStatus = (n) => {
    if (n === 1) return pickList.length === 0 ? 'All grabbed' : `${grabbedCount}/${pickList.length} grabbed`;
    if (n === 2) return queue.length === 0 ? (heldOrders.length ? `${heldOrders.length} on preorder hold` : 'All packed') : `${queue.length} to pack${heldOrders.length ? ` · ${heldOrders.length} held` : ''}`;
    return packedReady.length === 0 ? 'Up to date' : `${packedReady.length} ready`;
  };
  const stepDone = (n) => {
    if (n === 1) return pickList.length > 0 && grabbedCount === pickList.length;
    if (n === 2) return queue.length === 0;
    return false;
  };

  return (
    <div className="space-y-8" data-testid="fulfillment-panel">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream">Fulfillment</h2>
          <p className="text-alp-gray text-sm mt-1">Three simple steps: pick your stock, pack one order at a time, then export to Australia Post.</p>
        </div>
        <button onClick={load} data-testid="fulfillment-refresh" className="inline-flex items-center gap-2 border border-alp-line text-alp-cream px-4 py-2 text-xs tracking-[0.15em] uppercase rounded hover:border-alp-gold hover:text-alp-gold transition-colors"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="fulfillment-stepper">
        {STEPS.map((s) => (
          <button key={s.n} onClick={() => setStep(s.n)} data-testid={`step-btn-${s.n}`}
            className={`rounded-lg border p-3 sm:p-4 text-left transition-colors ${step === s.n ? 'border-alp-gold bg-alp-gold/10' : 'border-alp-line bg-alp-card/30 hover:border-alp-gold/50'}`}>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${stepDone(s.n) ? 'bg-emerald-500 text-alp-black' : step === s.n ? 'bg-alp-gold text-alp-black' : 'bg-alp-line text-alp-gray'}`}>
                {stepDone(s.n) ? <Check size={13} /> : s.n}
              </span>
              <span className={`text-sm font-semibold tracking-[0.08em] uppercase ${step === s.n ? 'text-alp-gold' : 'text-alp-cream'}`}>{s.label}</span>
            </div>
            <div className="text-alp-gray text-[11px] mt-1.5 hidden sm:block">{s.sub}</div>
            <div className={`text-xs mt-1 ${stepDone(s.n) ? 'text-emerald-400' : 'text-alp-gray'}`} data-testid={`step-status-${s.n}`}>{stepStatus(s.n)}</div>
          </button>
        ))}
      </div>

      <QuickTracking orders={orders} onSaved={(updated) => { replaceOrder(updated); if (onOrderShipped) onOrderShipped(updated); }} />

      {loading ? (
        <p className="text-alp-gray py-12 text-center">Loading orders...</p>
      ) : (
        <>
          {/* STEP 1 — PICK */}
          {step === 1 && (
            <div className="space-y-6">
              {queue.length === 0 ? (
                <div className="border border-alp-line/60 rounded-lg bg-alp-card/20 py-16 text-center" data-testid="fulfillment-empty">
                  <PackageCheck size={30} className="text-alp-gold mx-auto mb-3" />
                  <p className="text-alp-cream">Nothing to pick right now.</p>
                  <p className="text-alp-gray text-sm mt-1">Orders appear here once you mark them <span className="text-alp-cream">Payment Confirmed</span>.</p>
                </div>
              ) : (
                <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-6" data-testid="pick-list">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <ShoppingBag size={18} className="text-alp-gold" />
                      <h3 className="font-serif text-xl text-alp-cream">Pick List &mdash; grab these out</h3>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-alp-gray text-sm">{grabbedCount}/{pickList.length} grabbed</span>
                      <button onClick={resetGrab} data-testid="pick-reset" className="text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-alp-gold transition-colors">Reset</button>
                    </div>
                  </div>
                  <p className="text-alp-gray text-xs mb-4">Totals across all {queue.length} order{queue.length === 1 ? '' : 's'} ready to pack. Digital and preorder items excluded.</p>
                  <div className="space-y-2">
                    {pickList.map((p) => {
                      const isGrabbed = !!grabbed[p.key];
                      return (
                        <button key={p.key} onClick={() => toggleGrab(p.key)} data-testid={`pick-row-${p.key}`}
                          className={`w-full flex items-center gap-3 rounded px-4 py-3.5 border text-left transition-colors ${isGrabbed ? 'bg-alp-gold/10 border-alp-gold/40' : 'bg-alp-dark/50 border-alp-line hover:border-alp-gold/50'}`}>
                          {isGrabbed ? <CheckCircle2 size={22} className="text-alp-gold shrink-0" /> : <Circle size={22} className="text-alp-gray shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className={`text-base font-medium ${isGrabbed ? 'text-alp-gold line-through' : 'text-alp-cream'}`}>{p.name}</span>
                            {p.size ? <span className="text-alp-gray text-xs ml-2">{p.size}</span> : null}
                            <div className="text-alp-gray text-[11px]">across {p.orderCount} order{p.orderCount === 1 ? '' : 's'}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-serif text-3xl text-alp-gold">{p.totalQty}</span>
                            <div className="text-alp-gray text-[10px] tracking-[0.15em] uppercase">to grab</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {queue.length > 0 && (
                <button onClick={() => setStep(2)} data-testid="step-to-pack"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded py-4 text-sm tracking-[0.2em] uppercase font-semibold transition-colors ${grabbedCount === pickList.length ? 'bg-alp-gold text-alp-black hover:bg-alp-goldlight' : 'border border-alp-gold/50 text-alp-gold hover:bg-alp-gold/10'}`}>
                  Start packing <ChevronRight size={17} />
                </button>
              )}
            </div>
          )}

          {/* STEP 2 — PACK */}
          {step === 2 && (
            <div className="space-y-6" data-testid="packing-list">
              {queue.length === 0 ? (
                <div className="border border-emerald-500/40 rounded-lg bg-emerald-500/5 py-14 text-center">
                  <CheckCircle2 size={30} className="text-emerald-400 mx-auto mb-3" />
                  <p className="text-alp-cream text-lg">All orders packed!</p>
                  {heldOrders.length > 0 && <p className="text-amber-300 text-sm mt-1">{heldOrders.length} order{heldOrders.length === 1 ? '' : 's'} waiting on preorder below.</p>}
                  <button onClick={() => setStep(3)} data-testid="step-to-export" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-3 rounded text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Continue to export <ChevronRight size={15} /></button>
                </div>
              ) : (
                <>
                  <div className="flex justify-end">
                    <button onClick={() => printPackingSlips(queue, slipNote)} data-testid="print-all-slips-btn"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded text-xs tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors">
                      <Printer size={14} /> Print all {queue.length} slips
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {queue.map((o) => {
                      const { done, total } = orderProgress(o);
                      const cust = o.customer || {};
                      const preCount = physicalItems(o).filter((it) => it.preorder).length;
                      const freeCount = (o.freebies || []).length;
                      return (
                        <div key={o.orderNumber} data-testid={`pack-order-${o.orderNumber}`} className="border border-alp-line rounded-lg bg-alp-card/40 p-5 flex flex-col">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <div className="font-serif text-xl text-alp-cream truncate">{cust.fullName || 'Customer'}</div>
                              <div className="text-alp-gray text-xs">{o.orderNumber}{cust.suburb || cust.city ? ` \u00b7 ${cust.suburb || cust.city}, ${cust.state || ''}` : ''}</div>
                            </div>
                            <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded border shrink-0 ${done === total && total > 0 ? 'text-emerald-400 border-emerald-500/40' : 'text-alp-gray border-alp-line'}`}>{done}/{total}</span>
                          </div>
                          <div className="text-alp-gray text-sm mb-3 flex-1">
                            {physicalItems(o).map((it) => `${it.qty}\u00d7 ${it.name}`).join(' \u00b7 ')}
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {preCount > 0 && <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-amber-300 border border-amber-500/40 rounded px-2 py-0.5"><Clock size={11} /> {preCount} preorder</span>}
                            {freeCount > 0 && <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/40 rounded px-2 py-0.5"><Gift size={11} /> {freeCount} freebie{freeCount === 1 ? '' : 's'}</span>}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => printPackingSlip(o, slipNote)} data-testid={`print-slip-${o.orderNumber}`} title="Print packing slip"
                              className="shrink-0 px-3 rounded border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors">
                              <Printer size={15} />
                            </button>
                            {preCount > 0 && (
                              <button onClick={() => holdOrder(o)} disabled={busy} data-testid={`hold-order-${o.orderNumber}`} title="Move to preorder list — pack later"
                                className="shrink-0 inline-flex items-center gap-1.5 px-3 rounded border border-amber-500/50 text-amber-300 text-[10px] tracking-[0.12em] uppercase hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                                <Clock size={13} /> Pack later
                              </button>
                            )}
                            <button onClick={() => setFocusOrderNumber(o.orderNumber)} data-testid={`pack-now-${o.orderNumber}`}
                              className="flex-1 inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black rounded py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                              <PackageCheck size={15} /> Pack this order
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-alp-gold transition-colors"><ChevronLeft size={14} /> Back to pick list</button>
                </>
              )}

              {heldOrders.length > 0 && (
                <div className="border border-amber-500/40 rounded-lg bg-amber-500/5 p-5" data-testid="preorder-hold-list">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={16} className="text-amber-300" />
                    <h3 className="font-serif text-lg text-alp-cream">Waiting on preorder ({heldOrders.length})</h3>
                  </div>
                  <p className="text-alp-gray text-xs mb-4">Skipped from the packing queue — pack these once the preorder stock lands.</p>
                  <div className="space-y-2">
                    {heldOrders.map((o) => {
                      const cust = o.customer || {};
                      const pre = physicalItems(o).filter((it) => it.preorder);
                      return (
                        <div key={o.orderNumber} data-testid={`held-order-${o.orderNumber}`} className="flex flex-wrap items-center gap-3 border border-alp-line rounded bg-alp-dark/50 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-alp-cream text-sm truncate">{cust.fullName || 'Customer'} <span className="text-alp-gray text-xs">· {o.orderNumber}</span></div>
                            <div className="text-amber-300/90 text-xs truncate">
                              {pre.length > 0 ? `Waiting on: ${pre.map((it) => `${it.qty}× ${it.name}`).join(' · ')}` : physicalItems(o).map((it) => `${it.qty}× ${it.name}`).join(' · ')}
                            </div>
                          </div>
                          <button onClick={() => releaseOrder(o)} data-testid={`release-order-${o.orderNumber}`}
                            className="text-[10px] tracking-[0.12em] uppercase border border-alp-line text-alp-gray px-3 py-2 rounded hover:border-alp-gold hover:text-alp-gold transition-colors">
                            Back to queue
                          </button>
                          <button onClick={() => releaseOrder(o, true)} data-testid={`pack-held-${o.orderNumber}`}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase bg-alp-gold text-alp-black px-3 py-2 rounded font-semibold hover:bg-alp-goldlight transition-colors">
                            <PackageCheck size={13} /> Pack now
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — EXPORT */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-6" data-testid="export-summary">
                <div className="flex items-center gap-2 mb-2">
                  <Download size={18} className="text-alp-gold" />
                  <h3 className="font-serif text-xl text-alp-cream">Send to Australia Post</h3>
                </div>
                <p className="text-alp-gray text-sm mb-5">
                  <span className="text-alp-cream font-medium">{packedReady.length}</span> packed order{packedReady.length === 1 ? '' : 's'} ready to lodge
                  {alreadyExported > 0 && <span> &middot; <span className="text-alp-gold">{alreadyExported}</span> already exported</span>}.
                  Download the CSV then upload it in MyPost Business &rarr; Import orders.
                </p>
                <button onClick={() => exportAuspost(true)} disabled={exporting || packedReady.length === 0} data-testid="export-auspost-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded py-4 text-sm tracking-[0.2em] uppercase font-semibold bg-alp-gold text-alp-black hover:bg-alp-goldlight transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <Download size={17} /> {exporting ? 'Exporting...' : packedReady.length === 0 ? 'No new to export' : `Export ${packedReady.length} to Australia Post`}
                </button>
                {alreadyExported > 0 && (
                  <button onClick={() => exportAuspost(false)} disabled={exporting} data-testid="reexport-all-btn" className="mt-3 w-full text-center text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-alp-gold transition-colors underline underline-offset-4">Re-export all packed</button>
                )}
              </div>
              {queue.length > 0 && (
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-alp-gold transition-colors"><ChevronLeft size={14} /> {queue.length} order{queue.length === 1 ? '' : 's'} still to pack</button>
              )}
            </div>
          )}
        </>
      )}

      {/* FOCUS MODE */}
      {focusOrder && (
        <PackFocusMode
          order={focusOrder}
          position={focusPosition || 1}
          total={queue.length}
          products={products}
          slipNote={slipNote}
          packed={packed}
          itemKey={itemKey}
          onToggleItem={toggleItem}
          onTogglePreorder={togglePreorder}
          onAddFreebie={addFreebie}
          onRemoveFreebie={removeFreebie}
          pkg={pkgDraft[focusOrder.orderNumber] || defaultPkg(focusOrder)}
          onPreset={setPreset}
          onPkgField={setPkgField}
          onPkgCommit={commitPkgField}
          onComplete={completeOrder}
          onHold={holdOrder}
          onClose={() => setFocusOrderNumber(null)}
          busy={busy}
        />
      )}
    </div>
  );
}
